import type { FileFormatInfo } from '../utils/FileFormats';
import type { TelemetryEventQueue } from '../telemetry/MatomoUtils';
import { ShareTask } from './ShareTask';
import { shareObxHandler } from './handler/ShareObxHandler';

export interface ShareHook {
    /**
     * Will be executed before the first share content is generated.
     */
    beforeFirstContent?: (shareTask: ShareTask) => Promise<void>;

    /**
     * Will be executed after the last share content was created.
     */
    afterLastContent?: (shareTask: ShareTask) => Promise<void>;
}

export interface ShareOptions {
    itemIds: ReadonlyArray<string>;
    formats: ReadonlySet<string>;
    projectName: string;
    articleName?: string;
    shareAsLink?: boolean;
    telemetryEventQueue?: TelemetryEventQueue;
}

export const enum ShareContentSource {
    REMOTE_URL,
    APP_API_PATH,
    LOCAL_CONTENT
}

export type ShareContent = {
    source: ShareContentSource.APP_API_PATH | ShareContentSource.REMOTE_URL,
    content: string;
    filename: string;
    filetype: FileFormatInfo;
} | {
    source: ShareContentSource.LOCAL_CONTENT,
    content: string | ArrayBuffer | Blob;
    filename: string;
    filetype: FileFormatInfo;
};

/**
 * Note: the callback should return an empty array if the content could not be created
 */
type ShareContentCallback = (task: ShareTask, formats: ReadonlyArray<string>) => AsyncIterableIterator<ShareContent>;

export interface ShareContentGenerator {
    formats: ReadonlyArray<string> | undefined;
    callback: ShareContentCallback;
}

export const ShareManager = new class ShareManager {
    private hooks: Array<ShareHook> = [];
    private callbacks: Array<ShareContentGenerator> = [];

    public constructor() {
        // register some simple default handlers
        this.registerShareContent(['obx'], shareObxHandler);
    }

    /**
     * This function is used to register provider functions used for sharing content.
     *
     * @param formats a list of sharing keys handled by the callback; use `undefined` to get all selected formats
     * @param callback the content provide
     */
    public registerShareContent(formats: Array<string> | undefined, callback: ShareContentCallback): void {
        this.callbacks.push({
            formats,
            callback
        });
    }

    public registerHook(hook: ShareHook): void {
        this.hooks.push(hook);
    }

    public async shareContent(
        itemIds: ReadonlyArray<string>,
        formats: ReadonlyArray<string>,
        options: Omit<ShareOptions, 'itemIds' | 'formats'>
    ): Promise<ShareTask | null> {
        return this.getShareTask({
            ...options,
            formats: new Set(formats),
            itemIds
        });
    }

    private getShareTask(options: ShareOptions): ShareTask {
        return new ShareTask(options, this.callbacks, this.hooks);
    }
};